import type { ScenarioSuggestion } from 'src/components/star-grid/datasets/datasetTypes';

/** The token a scenario value's year becomes, matching the service's `YEAR_PLACEHOLDER`. */
export const YEAR_PLACEHOLDER = '{year}';
const YEAR = /(?<!\d)(20\d\d)(?!\d)/g;

/** One row of the Scenarios step: a value the map can read, and what it reads today. */
export interface ScenarioRow {
  /** The value as the map stores it: the dataset's own, or with its year as `{year}`. */
  readonly read: string;
  /** The dataset's values this row covers (one per year for a `{year}` row). */
  readonly values: readonly string[];
  readonly first: number | null;
  readonly last: number | null;
  /** Reporting scenarios mapped to this row now. */
  readonly reporting: readonly string[];
  /** The suggestions behind this row, with their reasons. */
  readonly suggested: readonly ScenarioSuggestion[];
}

/** `value` with its one year as `{year}` (`2024_Actuals` -> `{year}_Actuals`); itself otherwise. */
export function readFor(value: string): string {
  const years = value.match(YEAR) ?? [];
  return years.length === 1 ? value.replace(years[0], YEAR_PLACEHOLDER) : value;
}

/**
 * Rows grouped the way the map reads them: values differing only by year are one `{year}` row,
 * so a mapping holds for every year. Months span the row's values.
 */
export function scenarioRows(
  values: readonly string[],
  months: Readonly<Record<string, readonly [number, number]>>,
  suggestions: readonly ScenarioSuggestion[],
  scenarioMap: Readonly<Record<string, string>>
): ScenarioRow[] {
  const grouped = new Map<string, string[]>();
  values.forEach((value) => grouped.set(readFor(value), [...(grouped.get(readFor(value)) ?? []), value]));
  return Array.from(grouped, ([read, members]) => {
    const spans = members.map((value) => months[value]).filter((span): span is readonly [number, number] => Boolean(span));
    return {
      read,
      values: members,
      first: spans.length ? Math.min(...spans.map((span) => span[0])) : null,
      last: spans.length ? Math.max(...spans.map((span) => span[1])) : null,
      reporting: Object.keys(scenarioMap).filter((name) => scenarioMap[name] === read || members.includes(scenarioMap[name])),
      suggested: suggestions.filter((suggestion) => suggestion.value === read || members.includes(suggestion.value))
    };
  });
}

/** The map with exactly `reporting` reading `read`. */
export function mapRow(scenarioMap: Readonly<Record<string, string>>, row: ScenarioRow, reporting: readonly string[]): Record<string, string> {
  const kept = Object.entries(scenarioMap).filter(([, value]) => value !== row.read && !row.values.includes(value));
  return Object.fromEntries([...kept, ...reporting.map((name) => [name, row.read] as const)]);
}

/** `202401`–`202412` as `2024-01 – 2024-12`; one month alone when both are the same. */
export function monthSpan(first: number, last: number): string {
  const month = (yyyymm: number): string => `${Math.floor(yyyymm / 100)}-${String(yyyymm % 100).padStart(2, '0')}`;
  return first === last ? month(first) : `${month(first)} – ${month(last)}`;
}
